import React, { useEffect, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Progress } from '@/components/ui/progress';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { AlertTriangle, Trophy, Send } from 'lucide-react';
import api, { fmtIDR, currentMonth } from '@/lib/api';
import { toast } from 'sonner';

export default function LeaderboardView() {
  const [month, setMonth] = useState(currentMonth());
  const [board, setBoard] = useState(null);
  const [warnFor, setWarnFor] = useState(null);
  const [msg, setMsg] = useState('');

  const load = useCallback(async () => {
    try {
      const { data } = await api.get(`/kpi/leaderboard?month=${month}`);
      setBoard(data);
    } catch (e) { console.error('[Leaderboard] gagal load:', e); }
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const sendWarn = async () => {
    if (!warnFor) return;
    try {
      await api.post('/notifications/warnings', { salesman_id: warnFor.salesman_id, message: msg });
      toast.success(`Teguran terkirim ke ${warnFor.salesman_name}`);
      setWarnFor(null); setMsg('');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal kirim teguran'); }
  };

  return (
    <div className="space-y-6">
      <div className="max-w-xs">
        <Label>Bulan</Label>
        <Input type="month" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="leaderboard-month" />
      </div>

      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><Trophy className="h-5 w-5 text-amber-500" />Leaderboard {board?.month}</CardTitle></CardHeader>
        <CardContent className="p-0">
          <Table data-testid="leaderboard-table">
            <TableHeader>
              <TableRow>
                <TableHead>#</TableHead>
                <TableHead>Salesman</TableHead>
                <TableHead className="text-right">Omset</TableHead>
                <TableHead className="text-right">Target</TableHead>
                <TableHead>Progress</TableHead>
                <TableHead className="text-right">Komisi</TableHead>
                <TableHead>Aksi</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(board?.entries || []).length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-sm text-muted-foreground">Belum ada salesman aktif</TableCell></TableRow>
              ) : (board?.entries || []).map((e, i) => (
                <TableRow key={e.salesman_id} data-testid={`leaderboard-row-${e.salesman_id}`}>
                  <TableCell><Badge variant={i === 0 ? 'default' : 'secondary'}>{i + 1}</Badge></TableCell>
                  <TableCell className="font-medium">{e.salesman_name}</TableCell>
                  <TableCell className="text-right tabular-nums">{fmtIDR(e.revenue)}</TableCell>
                  <TableCell className="text-right tabular-nums text-muted-foreground">{e.target > 0 ? fmtIDR(e.target) : '—'}</TableCell>
                  <TableCell className="w-48">
                    {e.target > 0 ? (
                      <div className="space-y-1">
                        <Progress value={Math.min(100, e.progress_pct)} className="h-2" />
                        <div className="text-xs text-muted-foreground">{e.progress_pct}%</div>
                      </div>
                    ) : <span className="text-xs text-muted-foreground">Belum ada target</span>}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="tabular-nums text-emerald-700 font-medium">{fmtIDR(e.commission)}</div>
                    {e.commission_breakdown && (
                      <div className="text-[10px] text-muted-foreground tabular-nums" data-testid={`leaderboard-breakdown-${e.salesman_id}`}>
                        Non-Besi <b className="text-foreground">{fmtIDR(e.commission_breakdown.non_iron || 0)}</b>
                        <span className="mx-1">·</span>
                        Besi <b className="text-amber-700">{fmtIDR(e.commission_breakdown.iron || 0)}</b>
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    {e.below_target ? (
                      <Button size="sm" variant="destructive" onClick={() => setWarnFor(e)} data-testid={`send-warning-${e.salesman_id}`}>
                        <AlertTriangle className="h-3.5 w-3.5 mr-1.5" /> Kirim Teguran
                      </Button>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">On Track</Badge>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!warnFor} onOpenChange={(o) => !o && setWarnFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Kirim Teguran</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="text-sm">Kepada: <span className="font-medium">{warnFor?.salesman_name}</span></div>
            <div>
              <Label>Pesan (opsional)</Label>
              <Textarea value={msg} onChange={(e) => setMsg(e.target.value)} placeholder="Mohon tingkatkan performa Anda bulan ini..." rows={4} data-testid="warning-message" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setWarnFor(null)}>Batal</Button>
            <Button onClick={sendWarn} data-testid="warning-send-button"><Send className="h-4 w-4 mr-2" />Kirim</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
